import { Injectable, OnDestroy } from '@angular/core';

import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { SharedReportApi } from '../../../api/reports/shared-report.api';
import { ConfirmDialogComponent } from '../../Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { ConfirmDialogModel } from '../../Dialogs/models/confirm-dialog.model';
import { messageSeverityEnum } from '../../Misc/enums/message-severity.enum';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { ChartDisplayElementModel } from '../models/chart-display-element.model';
import { SharedReportListItemModel } from '../models/shared-report-list-item.model';
import { SharedReportModel } from '../models/shared-report.model';
import { SharedReportSearchService } from './shared-report-search.service';

@Injectable()
export class SharedReportService implements OnDestroy {

    private subscriptions: Subscription[];
    private dialogID: string;
    public selectedItems: SharedReportModel[] = [];
    private deleteLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    /**
     * Class Constructor
     */
    constructor(
        private coreDialogService: CoreDialogService,
        private sharedReportApi: SharedReportApi,
        private sharedReportSearchService: SharedReportSearchService,
        private messageService: MessageService,
        private localizationService: LocalizationService
    ) {}

    /**
    * Sets subscriptions, pushing to array for easy cleanup later
    */
    private setSubscriptions(): void {
        this.subscriptions = [];
    }

    getDeleteLoading(): Observable<boolean> {
        return this.deleteLoading$;
    }

    /**
    * this function has 2 functionalities (localize labels & clean up data)
    *
    * 1) the Shared Reports repo doesn't localize, but some localization (non-transformed) data might be present
    * transform it before sending to the api
    *
    * 2) when you create a shared report.. the data being send to the api is to big..
    * and it looks like the viewUrl for every data point is taking up a lot of room, so this function removed it
    */
    serializeSharedReport(sharedReport: SharedReportModel): void {
        sharedReport.charts?.forEach(block => {

            (<ChartDisplayElementModel>block.displayElement).series.forEach(series => {
                // 1. localize subseries labels
                series?.subSeries?.forEach(subSeries => {
                    subSeries.label = this.localizationService.transform(subSeries.label);
                });

                // 2. remove viewUrl from each data point
                series?.dataProvider?.value?.forEach(dataPoint => {
                    delete dataPoint?.viewUrl;
                });
            });

        });
    }

    createSharedReport(sharedReport: SharedReportModel): Observable<number> {
        this.serializeSharedReport(sharedReport);

        const sharedReportApiModel = sharedReport.toApiCreateModel();
        // reset block id to perform creating shared report
        const data = JSON.parse(sharedReportApiModel.data);
        if (data?.blocks) {
            data.blocks.forEach(block => block.id = undefined);
            sharedReportApiModel.data = JSON.stringify(data);
        }
        return this.sharedReportApi.create(sharedReportApiModel);
    }

    sendInvites(sharedReport: SharedReportModel): Observable<void> {
        return this.sharedReportApi.send(
            sharedReport.id,
            sharedReport.getSendModel(
                this.localizationService.transform('Reports.sharedReportInviteDialog.emailSubject', { name: sharedReport.name })
            )
        );
    }

    deleteSharedReports(deleteItems: (SharedReportModel|SharedReportListItemModel)[]): void {
        const confirmDialogModel: ConfirmDialogModel = {
            inputs: {
                id: 'sharedReports',
                heading: 'Reports.sharedReports.deleteDialog.heading',
                alertType: messageSeverityEnum.error,
                alertMessage: 'Reports.sharedReports.deleteDialog.alertMessage',
                showCancel: true,
                message: 'Reports.sharedReports.deleteDialog.message',
                list: deleteItems.map(item => item.name),
                okLabel: 'Reports.sharedReports.deleteDialog.okLabel'
            },
            outputs: {
                dialogClose: (confirm: boolean) => {
                    if (confirm) {
                        this.deleteLoading$.next(true);
                        combineLatest(deleteItems.map(item => this.sharedReportApi.delete(item.id))).subscribe({
                            next: () => {
                                this.messageService.add({
                                    severity: messageSeverityEnum.success,
                                    summary: this.localizationService.transform('Reports.sharedReports.deleteDialog.successHeading'),
                                    detail: this.localizationService.transform('Reports.sharedReports.deleteDialog.successDetail')
                                });
                                this.deleteLoading$.next(false);
                                this.sharedReportSearchService.loadListAndSearch();
                            },
                            error: () => {
                                this.messageService.add({
                                    severity: messageSeverityEnum.error,
                                    summary: this.localizationService.transform('Reports.sharedReports.deleteDialog.errorHeading'),
                                    detail: this.localizationService.transform('Reports.sharedReports.deleteDialog.errorDetail')
                                });
                                this.deleteLoading$.next(false);
                                this.sharedReportSearchService.loadListAndSearch();
                            }
                        });
                    }
                    this.dialogID = null;
                }
            }
        };

        this.dialogID = this.coreDialogService.open(ConfirmDialogComponent, confirmDialogModel);
    }

    updateSharedReport(sharedReport: SharedReportModel): Observable<number> {
        this.serializeSharedReport(sharedReport);
        return this.sharedReportApi.update(sharedReport.toApiUpdateModel());
    }

    getReport(id: number): Observable<SharedReportModel> {
        return this.sharedReportApi.get(id).pipe(map(response => new SharedReportModel(response)));
    }

    /**
     * Garbage collector for subscriptions
     */
    ngOnDestroy(): void {
        this.subscriptions.forEach(
            subscription => {
                subscription.unsubscribe();
            }
        );
    }
}
